import { CobaltLoaderLinear } from '@cae/cobalt-react/components/loader-linear';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { NotFoundIllustration } from '@shared/components/feedback-content/illustrations/NotFoundIllustration';
import { getFullName } from '@shared/helpers/get-full-name/getFullName';
import * as React from 'react';

import type { SalesforceRedirectContentInnerProps } from './SalesforceRedirectContentInner.types';

import { BlockingDiscrepancyContent } from '../blocking-discrepancy-content/BlockingDiscrepancyContent';
import styles from '../content/SalesforceRedirectContent.module.css';
import { EmailDiscrepancyContent } from '../email-discrepancy-content/EmailDiscrepancyContent';
import { AircraftIllustration } from '../illustrations/AircraftIllustration';
import { LoadErrorBlock } from '../load-error-block/LoadErrorBlock';
import { NameDiscrepancyContent } from '../name-discrepancy-content/NameDiscrepancyContent';
import { NoPermissionsBlock } from '../no-permissions-block/NoPermissionsBlock';
import { OrgCreateErrorBlock } from '../org-create-error-block/OrgCreateErrorBlock';

const SalesforceRedirectContentInner = React.memo(
  ({
    state,
    importOrganization,
    syncUser,
    retry,
    onGoHome,
  }: Readonly<SalesforceRedirectContentInnerProps>): React.ReactElement => {
    if (state.step === 'no-permissions') {
      return <NoPermissionsBlock />;
    }

    if (state.step === 'discrepancy-sf-duplication') {
      return (
        <BlockingDiscrepancyContent
          title="It looks like there is a problem with the contact you selected."
          onGoHome={onGoHome}
        >
          <p className="body-02">
            Please open an IT ticket providing the name, account and email of
            the contact you are trying to add to CAE Connect.
          </p>
        </BlockingDiscrepancyContent>
      );
    }

    if (state.step === 'discrepancy-email-duplication') {
      const selectedName = getFullName(
        state.contactResponse.sfContact.firstName,
        state.contactResponse.sfContact.middleName,
        state.contactResponse.sfContact.lastName
      );

      return (
        <BlockingDiscrepancyContent
          title="It looks like there is another contact using the email of the contact you selected."
          onGoHome={onGoHome}
        >
          <p className="body-02">
            To resolve this, you may change the email of the Salesforce contact
            you selected ({selectedName}) to a new email.
          </p>
          <p className="body-02">
            Alternatively, you may open an IT ticket requesting to change the
            email of the existing contact or request that the contacts be
            merged.
          </p>
        </BlockingDiscrepancyContent>
      );
    }

    if (state.step === 'discrepancy-email') {
      const { caeConnectContact } = state.contactResponse;

      if (!caeConnectContact)
        return <LoadErrorBlock retry={retry} handleGoHome={onGoHome} />;

      return (
        <EmailDiscrepancyContent
          sfContact={state.contactResponse.sfContact}
          caeConnectContact={caeConnectContact}
          onGoHome={onGoHome}
        />
      );
    }

    if (state.step === 'discrepancy-name') {
      const { caeConnectContact } = state.contactResponse;

      if (!caeConnectContact)
        return <LoadErrorBlock retry={retry} handleGoHome={onGoHome} />;

      return (
        <NameDiscrepancyContent
          sfContact={state.contactResponse.sfContact}
          caeConnectContact={caeConnectContact}
          isSyncing={state.isSyncing}
          onSync={syncUser}
          onGoHome={onGoHome}
        />
      );
    }

    if (state.step === 'load-error') {
      return <LoadErrorBlock retry={retry} handleGoHome={onGoHome} />;
    }

    if (state.step === 'org-not-onboarded') {
      const { firstName, lastName } = state.contactResponse.sfContact;
      const userName = `${firstName} ${lastName}`;

      return (
        <FeedbackContent
          title={`${userName} belongs to ${state.org.organizationName} which is not yet onboarded in CAE Connect. Do you wish to create this organization?`}
          message="Creating an organization in CAE Connect will not send any communication to any contacts. Only users that are onboarded will receive an invitation email."
          illustration={<NotFoundIllustration />}
          primaryActionLabel="Create organization"
          onPrimaryAction={importOrganization}
        />
      );
    }

    if (state.step === 'importing-org') {
      const { firstName, lastName } = state.contactResponse.sfContact;
      const userName = `${firstName} ${lastName}`;

      return (
        <FeedbackContent
          title={`We are creating the ${state.org.organizationName} organization.`}
          message={`You will be able to add ${userName} on the following screen. Please wait a moment...`}
          illustration={<AircraftIllustration />}
        >
          <CobaltLoaderLinear className={styles.loading} state="loading" />
        </FeedbackContent>
      );
    }

    // state.step === 'org-create-error'
    return <OrgCreateErrorBlock org={state.org} retry={retry} />;
  }
);

SalesforceRedirectContentInner.displayName = 'SalesforceRedirectContentInner';

export { SalesforceRedirectContentInner };
